// =============================================================================
// USER UTILITIES FOR HANDLING DELETED USERS
// =============================================================================

export interface UserRecord {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  is_active?: boolean;
  deleted_at?: string | null;
  original_first_name?: string | null;
  original_last_name?: string | null;
  original_email?: string | null;
  avatar_url?: string | null;
}

/**
 * Get the display name for a user, handling deleted users properly
 */
export const getUserDisplayName = (user: UserRecord | null | undefined): string => {
  if (!user) {
    return "Unknown User";
  }

  // Build the base name
  let baseName = "";
  if (user.first_name && user.last_name) {
    baseName = `${user.first_name} ${user.last_name}`;
  } else if (user.first_name) {
    baseName = user.first_name;
  } else if (user.last_name) {
    baseName = user.last_name;
  } else if (user.email && !user.email.includes('deleted+')) {
    baseName = user.email.split('@')[0];
  } else {
    baseName = "Unknown User";
  }

  // Check if user is soft deleted and add [Inactive] suffix
  if (user.is_active === false || user.deleted_at) {
    return `${baseName} [Inactive]`;
  }

  return baseName;
};

/**
 * Get user initials for avatars, handling deleted users
 */
export const getUserInitials = (user: UserRecord | null | undefined): string => {
  if (!user) {
    return "?";
  }

  // Get base name without [Inactive] suffix for proper initials
  let baseName = "";
  if (user.first_name && user.last_name) {
    baseName = `${user.first_name} ${user.last_name}`;
  } else if (user.first_name) {
    baseName = user.first_name;
  } else if (user.last_name) {
    baseName = user.last_name;
  } else if (user.email && !user.email.includes('deleted+')) {
    baseName = user.email.split('@')[0];
  } else {
    baseName = "Unknown User";
  }
  
  // If name starts with "User" followed by an ID (fallback case)
  if (baseName.startsWith("User ")) {
    return "U";
  }

  // If name looks like an email (contains @), use first letter of local part
  if (baseName.includes("@")) {
    return baseName.split("@")[0].substring(0, 2).toUpperCase();
  }

  const parts = baseName.split(" ");
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  // For single names, use first two letters
  return baseName.substring(0, 2).toUpperCase();
};

/**
 * Check if a user is deleted
 */
export const isUserDeleted = (user: UserRecord | null | undefined): boolean => {
  return user?.is_active === false;
};

/**
 * Get the original display name for a deleted user (for admin purposes)
 */
export const getOriginalUserDisplayName = (user: UserRecord | null | undefined): string => {
  if (!user || !isUserDeleted(user)) {
    return getUserDisplayName(user);
  }

  // For deleted users, try to reconstruct original name
  if (user.original_first_name && user.original_last_name) {
    return `${user.original_first_name} ${user.original_last_name}`;
  } else if (user.original_first_name) {
    return user.original_first_name;
  } else if (user.original_last_name) {
    return user.original_last_name;
  } else if (user.original_email && !user.original_email.includes('deleted+')) {
    return user.original_email.split('@')[0];
  }

  return "Unknown User";
};

/**
 * Update existing getUserDisplayName functions throughout the codebase
 * This is a drop-in replacement for any existing getUserDisplayName logic
 */
export const formatUserDisplayName = (user: any): string => {
  return getUserDisplayName(user);
};

/**
 * Enhanced user profile type for components
 */
export interface UserProfile extends UserRecord {
  full_name?: string | null;
}

/**
 * Convert a user record to a user profile with full_name
 */
export const toUserProfile = (user: UserRecord): UserProfile => {
  return {
    ...user,
    full_name: getUserDisplayName(user)
  };
};

/**
 * Filter out deleted users from a list (useful for user selection dropdowns)
 */
export const filterActiveUsers = (users: UserRecord[]): UserRecord[] => {
  return users.filter(user => !isUserDeleted(user));
};

/**
 * Sort users with deleted users at the end
 */
export const sortUsersWithDeletedLast = (users: UserRecord[]): UserRecord[] => {
  return [...users].sort((a, b) => {
    // Active users first
    if (isUserDeleted(a) && !isUserDeleted(b)) return 1;
    if (!isUserDeleted(a) && isUserDeleted(b)) return -1;
    
    // Then sort by display name
    const nameA = getUserDisplayName(a).toLowerCase();
    const nameB = getUserDisplayName(b).toLowerCase();
    return nameA.localeCompare(nameB);
  });
};

/**
 * Get user avatar props for Avatar components
 */
export const getUserAvatarProps = (user: UserRecord | null | undefined) => {
  const displayName = getUserDisplayName(user);
  const initials = getUserInitials(user);
  
  return {
    src: (!isUserDeleted(user) && user?.avatar_url) ? user.avatar_url : undefined,
    alt: displayName,
    fallback: initials
  };
};

/**
 * Default export with all utilities
 */
export default {
  getUserDisplayName,
  getUserInitials,
  isUserDeleted,
  getOriginalUserDisplayName,
  formatUserDisplayName,
  toUserProfile,
  filterActiveUsers,
  sortUsersWithDeletedLast,
  getUserAvatarProps
}; 